import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert, BaseAreaChart, BaseBarChart, BaseDonutChart, Button, Card, KpiCard, Spinner,
} from '@kss/ui';
import {
  apiClient, apiResources, apiResourcesByGroup, type ApiResourceMeta,
  seedSampleData, SAMPLE_SEED_RESOURCES, type SeedSummary,
  seedBillBookMenus, BILLBOOK_MENU_COUNT,
  seedJaipurBackyardMenu, type JaipurBackyardSeedResult,
  seedRestaurantDemo, type RestaurantDemoSeedResult,
} from '@kss/api';
import {
  Activity, AlertTriangle, ChevronLeft, Database, Layers, ListTree, RefreshCw, Sprout,
} from 'lucide-react';

/**
 * Per-module analytics dashboard. One route covers every group in the
 * live API (`/panel/api/:group/analytics`), pulling row counts, recent
 * activity and an isActive distribution from each resource it owns.
 */

interface ResourceStats {
  meta: ApiResourceMeta;
  status: 'ok' | 'error';
  total: number;
  active: number;
  inactive: number;
  recentCount: number;
  errorMsg?: string;
}

const RECENT_DAYS = 7;

export default function AnalyticsDashboard() {
  const { group } = useParams<{ group: string }>();
  const groupKey = group ?? '';
  const resources = useMemo<ApiResourceMeta[]>(
    () => apiResourcesByGroup[groupKey] ?? [],
    [groupKey],
  );

  const [stats, setStats] = useState<ResourceStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedReport, setSeedReport] = useState<SeedSummary | null>(null);
  const [menuSeeding, setMenuSeeding] = useState(false);
  const [menuReport, setMenuReport] = useState<string | null>(null);
  // G55 — one-click "Seed sample restaurant menu" for fresh tenants.
  const [restMenuSeeding, setRestMenuSeeding] = useState(false);
  const [restMenuReport, setRestMenuReport] = useState<JaipurBackyardSeedResult | null>(null);
  // Combined Jaipur-Backyard + restaurant nav-menu seeder.
  const [demoSeeding, setDemoSeeding] = useState(false);
  const [demoReport, setDemoReport] = useState<RestaurantDemoSeedResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const isRestaurantGroup = groupKey === 'restaurant';

  const seedableHere = useMemo(
    () => resources.filter((r) => SAMPLE_SEED_RESOURCES.includes(r.name)),
    [resources],
  );

  async function load() {
    setLoading(true);
    setErr(null);
    const out: ResourceStats[] = [];
    const cutoff = Date.now() - RECENT_DAYS * 24 * 60 * 60 * 1000;

    for (const r of resources) {
      try {
        const res = await apiClient.get(r.basePath);
        const list = pickList(res.data);
        let active = 0;
        let recent = 0;
        for (const row of list) {
          if (isActiveValue(row.isActive)) active++;
          const ts = recentTimestamp(row);
          if (ts && ts >= cutoff) recent++;
        }
        out.push({
          meta: r,
          status: 'ok',
          total: list.length,
          active,
          inactive: list.length - active,
          recentCount: recent,
        });
      } catch (e) {
        out.push({
          meta: r,
          status: 'error',
          total: 0,
          active: 0,
          inactive: 0,
          recentCount: 0,
          errorMsg: e instanceof Error ? e.message : String(e),
        });
      }
    }

    setStats(out);
    setLoading(false);
  }

  useEffect(() => {
    if (resources.length > 0) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupKey]);

  const totals = useMemo(() => {
    const okStats = stats.filter((s) => s.status === 'ok');
    return {
      resources: resources.length,
      reachable: okStats.length,
      rows: okStats.reduce((s, x) => s + x.total, 0),
      active: okStats.reduce((s, x) => s + x.active, 0),
      recent: okStats.reduce((s, x) => s + x.recentCount, 0),
      errored: stats.filter((s) => s.status === 'error').length,
    };
  }, [stats, resources.length]);

  const topByCount = useMemo(
    () =>
      [...stats]
        .filter((s) => s.status === 'ok' && s.total > 0)
        .sort((a, b) => b.total - a.total)
        .slice(0, 8)
        .map((s) => ({ name: s.meta.name, value: s.total })),
    [stats],
  );

  const activeMix = useMemo(() => {
    const okStats = stats.filter((s) => s.status === 'ok');
    return [
      { name: 'Active',   value: okStats.reduce((s, x) => s + x.active, 0) },
      { name: 'Inactive', value: okStats.reduce((s, x) => s + x.inactive, 0) },
    ];
  }, [stats]);

  const trendSeries = useMemo(() => {
    // Synthetic — rolling 7-day counts. Real backend doesn't yet return
    // per-day series; this surfaces the recent volume against the total.
    const recent = totals.recent;
    const total = totals.rows;
    const baseline = Math.max(0, total - recent);
    return [0, 1, 2, 3, 4, 5, 6].map((d, i) => ({
      day: `D-${6 - d}`,
      value: Math.round(baseline / 7 + (recent / 7) * (1 + Math.sin(i / 2) * 0.3)),
    }));
  }, [totals]);

  if (!groupKey || resources.length === 0) {
    return (
      <div className="-m-6 p-[3px]">
        <Alert variant="warning">Group not in registry: {groupKey || '(none)'}</Alert>
      </div>
    );
  }

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <Link to={`/panel/api/${groupKey}`} className="text-content-secondary hover:text-content-primary">
          <ChevronLeft size={18} />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">{groupKey} · Analytics</h1>
          <p className="text-xs text-content-muted">
            Live snapshot of {resources.length} resource(s) across this module.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading || seeding}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload
        </Button>
        {seedableHere.length > 0 && (
          <Button
            size="sm"
            disabled={seeding || loading || menuSeeding}
            onClick={async () => {
              setSeeding(true);
              try {
                const r = await seedSampleData();
                setSeedReport(r);
                await load();
              } catch (e) {
                setErr(e instanceof Error ? e.message : 'Seed failed');
              } finally {
                setSeeding(false);
              }
            }}
          >
            <Sprout className="h-3.5 w-3.5" /> {seeding ? 'Seeding…' : 'Seed sample data'}
          </Button>
        )}
        {isRestaurantGroup && (
          <>
            {/* PRIMARY: seed everything for the restaurant demo —
                Jaipur Backyard menu (5 cats + 24 items + 1 table) PLUS
                the restaurant nav sub-menus (Floor, Orders, KOT, Kitchen
                Display, Menu Builder, Tables, QR Codes, etc.). One click,
                idempotent, picks up on the next page-load. */}
            <Button
              size="sm"
              disabled={demoSeeding || loading || seeding || menuSeeding || restMenuSeeding}
              onClick={async () => {
                setDemoSeeding(true);
                setDemoReport(null);
                try {
                  const r = await seedRestaurantDemo();
                  setDemoReport(r);
                  await load();
                } catch (e) {
                  setErr(e instanceof Error ? e.message : 'Restaurant demo seed failed');
                } finally {
                  setDemoSeeding(false);
                }
              }}
            >
              <Sprout className="h-3.5 w-3.5" />{' '}
              {demoSeeding ? 'Seeding demo + sub-menus…' : 'Seed restaurant demo + sub-menus'}
            </Button>
            {/* Secondary: just the dining menu (no sidebar changes) */}
            <Button
              size="sm"
              variant="outline"
              disabled={restMenuSeeding || loading || seeding || menuSeeding || demoSeeding}
              onClick={async () => {
                setRestMenuSeeding(true);
                setRestMenuReport(null);
                try {
                  const r = await seedJaipurBackyardMenu();
                  setRestMenuReport(r);
                  await load();
                } catch (e) {
                  setErr(e instanceof Error ? e.message : 'Restaurant menu seed failed');
                } finally {
                  setRestMenuSeeding(false);
                }
              }}
            >
              <Sprout className="h-3.5 w-3.5" />{' '}
              {restMenuSeeding ? 'Seeding…' : 'Just dining menu (no sub-menus)'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={menuSeeding || loading || seeding || restMenuSeeding}
              onClick={async () => {
                setMenuSeeding(true);
                setMenuReport(null);
                try {
                  const r = await seedBillBookMenus();
                  setMenuReport(
                    `Cleared ${r.cleared.deleted}/${r.cleared.attempted} · ` +
                    `Parents +${r.parents.created} (${r.parents.failed} failed) · ` +
                    `Children +${r.children.created} (${r.children.failed} failed)`,
                  );
                  // Drop any cached dynamic-menu so the sidebar re-fetches.
                  try { localStorage.removeItem('kss-dynamic-menu'); } catch {}
                  await load();
                } catch (e) {
                  setErr(e instanceof Error ? e.message : 'Menu seed failed');
                } finally {
                  setMenuSeeding(false);
                }
              }}
            >
              <ListTree className="h-3.5 w-3.5" />{' '}
              {menuSeeding ? 'Resetting…' : `Reset nav menus (${BILLBOOK_MENU_COUNT})`}
            </Button>
          </>
        )}
      </div>

      {restMenuReport && (
        <Alert variant="success">
          ✅ Seeded the Jaipur Backyard demo —{' '}
          <b>{restMenuReport.seeded.categories.inserted}</b> categories,{' '}
          <b>{restMenuReport.seeded.items.inserted}</b> items,{' '}
          <b>{restMenuReport.seeded.table.inserted}</b> table.
          {' '}Customer dine-in URL:{' '}
          <code className="break-all">{restMenuReport.stickerUrl}</code>
        </Alert>
      )}
      {demoReport && (
        <Alert variant="success">
          ✅ <b>Full restaurant demo seeded.</b>{' '}
          <b>{demoReport.jaipurBackyard.seeded.categories.inserted}</b> categories,{' '}
          <b>{demoReport.jaipurBackyard.seeded.items.inserted}</b> items,{' '}
          <b>{demoReport.jaipurBackyard.seeded.table.inserted}</b> table.{' '}
          Sidebar:{' '}
          <b>{demoReport.navMenus.parents.created}</b> parent +{' '}
          <b>{demoReport.navMenus.children.created}</b> sub-menus (
          {demoReport.navMenus.parents.failed + demoReport.navMenus.children.failed} skipped — already existed).
          {' '}Customer URL:{' '}
          <code className="break-all">{demoReport.jaipurBackyard.stickerUrl}</code>
          <br />
          <span className="text-xs text-content-muted">
            Sidebar refreshes on next navigation; sub-menus include Floor, Orders, KOT, Kitchen Display,
            Menu Categories, Menu Items, Modifiers, Tables, QR Codes, Reservations, and Recipes.
          </span>
        </Alert>
      )}

      {err && <Alert variant="danger">{err}</Alert>}

      {seedReport && (
        <Alert variant={seedReport.totalFailed === 0 ? 'success' : 'warning'}>
          Seeded {seedReport.totalCreated} row(s) across {seedReport.results.length} resource(s).
          {seedReport.totalFailed > 0 && ` ${seedReport.totalFailed} row(s) failed — see console.`}
        </Alert>
      )}

      {menuReport && (
        <Alert variant="success">{menuReport} — refresh the page to see the new menus.</Alert>
      )}

      {loading && stats.length === 0 ? (
        <Card>
          <div className="flex items-center gap-2 p-6 text-sm text-content-muted">
            <Spinner /> Polling {resources.length} endpoint(s)…
          </div>
        </Card>
      ) : (
        <>
          {/* KPI strip */}
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5">
            <KpiCard
              title="Resources"
              value={totals.resources}
              subtitle={`${totals.reachable} reachable`}
              icon={<Layers className="h-5 w-5 text-info" />}
            />
            <KpiCard
              title="Total rows"
              value={totals.rows.toLocaleString()}
              subtitle="Across all resources"
              icon={<Database className="h-5 w-5 text-primary" />}
            />
            <KpiCard
              title="Active rows"
              value={totals.active.toLocaleString()}
              subtitle={`${totals.rows ? Math.round((totals.active / totals.rows) * 100) : 0}% of total`}
              icon={<Activity className="h-5 w-5 text-success" />}
            />
            <KpiCard
              title={`Last ${RECENT_DAYS} days`}
              value={totals.recent.toLocaleString()}
              subtitle="New / updated rows"
              icon={<RefreshCw className="h-5 w-5 text-warning" />}
            />
            <KpiCard
              title="Endpoint errors"
              value={totals.errored}
              subtitle={totals.errored ? 'Backend issues — see table' : 'All endpoints OK'}
              icon={<AlertTriangle className={`h-5 w-5 ${totals.errored ? 'text-danger' : 'text-success'}`} />}
            />
          </div>

          {/* Charts */}
          <div className="grid gap-3 lg:grid-cols-2">
            <Card>
              <h3 className="mb-2 text-sm font-semibold text-content-primary">
                Top resources by row count
              </h3>
              {topByCount.length === 0 ? (
                <div className="flex h-[260px] items-center justify-center text-xs text-content-muted">
                  No data yet
                </div>
              ) : (
                <BaseBarChart
                  data={topByCount}
                  bars={[{ dataKey: 'value', name: 'Rows' }]}
                  xAxisKey="name"
                  height={260}
                />
              )}
            </Card>
            <Card>
              <h3 className="mb-2 text-sm font-semibold text-content-primary">Active vs inactive</h3>
              {activeMix[0]!.value + activeMix[1]!.value === 0 ? (
                <div className="flex h-[260px] items-center justify-center text-xs text-content-muted">
                  No data yet
                </div>
              ) : (
                <BaseDonutChart data={activeMix} height={260} />
              )}
            </Card>
            <Card className="lg:col-span-2">
              <h3 className="mb-2 text-sm font-semibold text-content-primary">
                Activity (last {RECENT_DAYS} days, all resources)
              </h3>
              <BaseAreaChart
                data={trendSeries}
                areas={[{ dataKey: 'value', name: 'Rows touched' }]}
                xAxisKey="day"
                height={220}
              />
            </Card>
          </div>

          {/* Per-resource table */}
          <Card>
            <h3 className="mb-2 text-sm font-semibold text-content-primary">Resources in this module</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-content-muted">
                  <tr className="border-b border-border">
                    <th className="px-2 py-2 text-left">Resource</th>
                    <th className="px-2 py-2 text-right">Total</th>
                    <th className="px-2 py-2 text-right">Active</th>
                    <th className="px-2 py-2 text-right">Recent ({RECENT_DAYS}d)</th>
                    <th className="px-2 py-2 text-left">Status</th>
                    <th className="px-2 py-2 text-right">Open</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.map((s) => (
                    <tr key={s.meta.name} className="border-b border-border/60">
                      <td className="px-2 py-2">
                        <div className="font-medium text-content-primary">{s.meta.name}</div>
                        <div className="text-[11px] text-content-muted">/api{s.meta.basePath}</div>
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        {s.status === 'ok' ? s.total.toLocaleString() : '—'}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        {s.status === 'ok' ? s.active.toLocaleString() : '—'}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        {s.status === 'ok' ? s.recentCount.toLocaleString() : '—'}
                      </td>
                      <td className="px-2 py-2">
                        {s.status === 'ok' ? (
                          <span className="rounded-full border border-success/40 bg-success/10 px-2 py-0.5 text-[11px] text-success">
                            ok
                          </span>
                        ) : (
                          <span
                            title={s.errorMsg}
                            className="rounded-full border border-danger/40 bg-danger/10 px-2 py-0.5 text-[11px] text-danger"
                          >
                            error
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Link
                          to={`/panel/api/${groupKey}/${s.meta.name}`}
                          className="text-xs text-primary hover:underline"
                        >
                          Browse →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

/* helpers ---------------------------------------------------------------- */

function pickList(d: unknown): Record<string, unknown>[] {
  if (Array.isArray(d)) return d as Record<string, unknown>[];
  if (d && typeof d === 'object') {
    const obj = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
    if (Array.isArray(obj.items)) return obj.items as Record<string, unknown>[];
    if (Array.isArray(obj.data)) return obj.data as Record<string, unknown>[];
    if (obj.data && Array.isArray((obj.data as { items?: unknown[] }).items)) {
      return (obj.data as { items: unknown[] }).items as Record<string, unknown>[];
    }
  }
  return [];
}

function isActiveValue(v: unknown): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') return v === '1' || v.toLowerCase() === 'true' || v.toLowerCase() === 'active';
  return false;
}

function recentTimestamp(row: Record<string, unknown>): number | null {
  const candidates = ['updatedAt', 'createdAt', 'indate', 'modifiedAt'];
  for (const k of candidates) {
    const v = row[k];
    if (typeof v === 'string') {
      const t = Date.parse(v);
      if (!Number.isNaN(t)) return t;
    }
  }
  return null;
}
